import React, { useContext, useEffect, useState } from 'react';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import { useToast } from 'components/ToastContainer';
import useCompanies from 'hooks/useCompanies';
import useFormikHandler from 'screens/Integrations/hooks/useFormikHandler';
import SubmissionFooter from 'screens/Integrations/components/SubmissionFooter';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import events from 'utilities/amplitude/events';

import { AgVendMatchContext } from '../context';
import { AgVendContext } from '../../context/Provider';
import {
  checkDuplicatedNamesInNewCompanies,
  checkMatchesDuplicates,
  createCompaniesInBatches,
  displayErrorToast,
  isNotMatched
} from '../helpers';
import AgVendMatchBoard from '../components/MatchBoard';

const AgVendRetailersMatch = () => {
  const [unmatchedRetailers, setUnmatchedRetailers] = useState([]);
  const [retailersToMatch, setRetailersToMatch] = useState([]);
  const [createNewRetailers, setCreateNewRetailers] = useState([]);
  const [isCreatingCompanies, setIsCreatingCompanies] = useState(false);
  const { triggerAnalyticsEvent } = useSubmissionAnalytics();
  const {
    loading: isLoadingCompanies,
    companies,
    loadCompanies,
    createCompany
  } = useCompanies();

  const { selectedInvoices } = useFormikHandler();

  const toast = useToast();

  const { releaseStepForward } = useContext(AgVendContext);
  const {
    isLoading,
    state: { matches },
    fetchMatches,
    submitMatches,
    updateMatches
  } = useContext(AgVendMatchContext);

  useEffect(() => {
    if (!isLoading) {
      fetchMatches();
    }
    setRetailersToMatch(
      selectedInvoices?.reduce((acc, cur) => {
        if (
          acc.findIndex(r => r.externalId === cur.retailerExternalId) === -1
        ) {
          acc.push({
            name: cur.retailerName,
            retailerName: cur.retailerName,
            externalId: cur.retailerExternalId
          });
        }
        return acc;
      }, [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    loadCompanies();
    setUnmatchedRetailers(
      retailersToMatch?.filter(
        retailer =>
          isNotMatched(matches, retailer, companies) &&
          !createNewRetailers.some(r => r.externalId === retailer.externalId)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, companies?.length, retailersToMatch, createNewRetailers]);

  // Handle creating new retailer - add/remove from createNewRetailers array
  const handleCreateNewRetailer = (retailer, isCreateNew) => {
    if (isCreateNew) {
      // Add retailer to createNewRetailers if not already present
      setCreateNewRetailers(prev => {
        if (!prev.some(r => r.externalId === retailer.externalId)) {
          return [...prev, retailer];
        }
        return prev;
      });
    } else {
      // Remove retailer from createNewRetailers
      setCreateNewRetailers(prev =>
        prev.filter(r => r.externalId !== retailer.externalId)
      );
    }
  };

  const onNextHandler = async () => {
    if (!checkMatchesDuplicates(matches, retailersToMatch)) {
      displayErrorToast(
        toast,
        'Unable to create matches',
        'The same Cropwise Company was selected. Please choose different Company for each match.'
      );
      return;
    }
    const duplicateNames = checkDuplicatedNamesInNewCompanies(
      companies,
      createNewRetailers
    );

    if (duplicateNames) {
      displayErrorToast(
        toast,
        'Unable to create companies',
        `The following retailer name(s) already exist as company: ${duplicateNames}. Please select them using the company dropdown instead of creating new ones.`
      );
      return;
    }

    // Create companies for retailers marked as "create new"
    const newCompanyMatches = [];
    if (createNewRetailers.length > 0) {
      setIsCreatingCompanies(true);

      try {
        const allBatchResults = await createCompaniesInBatches(
          createCompany,
          createNewRetailers
        );
        const newMatches = allBatchResults
          .filter(({ company }) => company.id && company.name)
          .map(({ retailer, company }) => ({
            vendorEntityDescription: retailer.retailerName,
            vendorEntityId: retailer.externalId,
            cwfEntityId: company.id,
            type: 'Company'
          }));

        newCompanyMatches.push(...newMatches);
      } catch {
        displayErrorToast(
          toast,
          'Unable to create companies',
          'There was an error creating some companies. Please try again.'
        );
        return;
      } finally {
        setIsCreatingCompanies(false);
      }
    }

    const allMatches = [...matches, ...newCompanyMatches];
    if (newCompanyMatches.length > 0) {
      updateMatches(allMatches);
    }

    await submitMatches(newCompanyMatches.length > 0 ? allMatches : null);

    triggerAnalyticsEvent(
      events.epic.Integrations.IncomingInvoice.invoiceMatchCompanies,
      {
        vendor: INTEGRATIONS.agVend
      }
    );
  };

  useEffect(() => {
    if (!unmatchedRetailers?.length) {
      releaseStepForward();
    }
  }, [unmatchedRetailers, releaseStepForward]);

  const nextDisabled =
    isLoading ||
    unmatchedRetailers?.length ||
    isLoadingCompanies ||
    isCreatingCompanies;

  return (
    <>
      <AgVendMatchBoard
        unMatchedEntities={unmatchedRetailers}
        entitiesToMatch={retailersToMatch}
        matchType="Company"
        title="Match Companies"
        description="Match your retailers to companies in your Cropwise Financials list."
        createNewRetailers={createNewRetailers}
        onCreateNewRetailer={handleCreateNewRetailer}
      />
      <SubmissionFooter
        onNext={onNextHandler}
        loading={isLoading || isCreatingCompanies || isLoadingCompanies}
        nextLabel="Next: Match Products"
        nextDisabled={nextDisabled}
        integrationType={INTEGRATIONS.agVend}
        isBottomFixed
        isLastStep={false}
        context={AgVendContext}
      />
    </>
  );
};

export default AgVendRetailersMatch;
